import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Loader2 } from 'lucide-react';
import { COLLECTIONS } from '@/constants/testIds';
import Placeholder from './Placeholder';
import { useCatalog } from '@/hooks/useCatalog';
import { productImageUrl } from '@/lib/imageUrl';

// Curated subtitle & tone per known category (extras render with a friendly default)
const META = {
  lehengas: { sub: 'Twirl-worthy silhouettes for every dance floor', tone: 'maroon' },
  sarees: { sub: 'Timeless drapes in silks, chiffons & organza', tone: 'gold' },
  bridal: { sub: 'Heirloom-worthy ensembles for the big day', tone: 'maroon' },
  family: { sub: 'Coordinated looks for the whole family', tone: 'blush' },
  kids: { sub: 'Little stars dressed in joy', tone: 'ivory' },
  accessories: { sub: 'Polki, potli bags & statement pieces', tone: 'gold' },
};

export const FeaturedCollections = () => {
  const { products, categories, loading } = useCatalog();

  // Only show categories that actually have products (auto-adds new admin categories)
  const items = categories
    .map((c) => {
      const catProducts = products.filter((p) => p.category === c.key);
      if (catProducts.length === 0) return null;
      // Prefer the admin-chosen "Category hero" image, else fall back to the
      // most recent product for that category.
      const hero = catProducts.find((p) => p.is_category_hero) || catProducts[0];
      const meta = META[c.key] || {};
      return {
        slug: c.key,
        title: c.label,
        sub: meta.sub || 'View the collection',
        tone: meta.tone || 'ivory',
        image: productImageUrl(hero),
        count: catProducts.length,
      };
    })
    .filter(Boolean);

  return (
    <section id="collections" data-testid={COLLECTIONS.root} className="py-24 lg:py-32 bg-surface">
      <div className="container-luxe">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <span className="overline">The Collections</span>
            <h2 className="font-display text-4xl lg:text-5xl xl:text-6xl text-charcoal mt-3 leading-[1.05] tracking-tight">
              Curated for every <em className="text-maroon not-italic font-medium">celebration</em>.
            </h2>
          </div>
          <p className="max-w-md text-charcoal/70 leading-relaxed">
            From bridal couture to playful kidswear, every piece is hand-picked for craftsmanship,
            comfort and an unmistakable Trendy Closet finish.
          </p>
        </div>

        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-maroon" /></div>
        ) : items.length === 0 ? (
          <div className="py-20 text-center text-charcoal/55">Collections coming soon.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {items.map((it, i) => (
              <motion.div
                key={it.slug}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.7, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link
                  to={`/category/${it.slug}`}
                  data-testid={COLLECTIONS.card(it.slug)}
                  className="group block relative"
                >
                  <div className="relative overflow-hidden">
                    <div className="transition-transform duration-[1200ms] ease-out group-hover:scale-105">
                      <Placeholder label={it.title} tone={it.tone} ratio="3/4" image={it.image} alt={it.title} />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-burgundy/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-ivory/90 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                      <ArrowUpRight className="w-4 h-4 text-maroon" />
                    </div>
                    <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 bg-ivory/90 backdrop-blur text-charcoal/80">
                      Trendy Closet
                    </div>
                  </div>
                  <div className="pt-5 flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-display text-xl text-charcoal tracking-tight">{it.title}</h3>
                      <p className="text-sm text-charcoal/60 mt-1">{it.sub}</p>
                    </div>
                    <span className="overline mt-2 whitespace-nowrap group-hover:text-maroon transition-colors">Explore →</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedCollections;
